import { CommonModule } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatStepperModule } from "@angular/material/stepper";
import { MatTableModule } from "@angular/material/table";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";
import { AggregatesModule } from "@tylertech/aggregates-module";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartingModule } from "@tylertech/charting-module";
import { CommandsModule } from "@tylertech/commands-module";
import { CommonComponentsModule } from "@tylertech/common-components-module";
import { CommonDialogsModule } from "@tylertech/common-dialogs-module";
import { ContactModule } from "@tylertech/contact-module";
import { CoreModule } from "@tylertech/core-module";
import { CustomViewModule } from "@tylertech/custom-view";
import { IncodeModule } from "@tylertech/incode-module";
import { LgdCheckboxModule, LgdDatePickerModule, LgdSelectModule, LgdTextAreaModule, LgdTextModule, LgdTypeAheadModule } from "@tylertech/lgd-reactive-forms";
import { SearchModule } from "@tylertech/search-module";
import
{
    CatchUpBillingActionHandler,
    LeakNotificationNotSentActionHandler,
    PendingPaymentPlanActionHandler,
    TylerSmartMeterActionHandler,
    UtilityServiceRequestActionHandler,
} from "./aggregateActionHandlers";
import
{
        /* Just components */
        AddTodoItemDialogComponent,
        /* Dialogs */
        ContactMatchDialogComponent,
        ExternalLinkPopUpDialogComponent,
        NewPacketTemplateDialogComponent,
        /* Smart-Cards && Chart-like things */
        NotificationHistoryChartComponent,
        OutstandingServiceOrdersChartComponent,
        PacketProgressChartComponent,
        PacketsProgressChartsComponent,
        PrepaidBillingChartComponent,
        PrepaidConsumptionChartComponent,
        QuickLinksChartComponent,
        ServiceAddressWizardDialogComponent,
        ServiceRequestRejectDialogComponent,
        ServiceRequestTextEmailDialogComponent,
        SmartMeterPortalUsageChartComponent,
        TotalCommunityConsumptionVsMeterChartComponent,
        TotalMeterReportingVsMeterReportingChartComponent,
        TylerSmartMeterTextEmailDialogComponent,
        UtilitiesBillCycleChartComponent,
        UtilitiesConnectingDisconnectingAccountsChartComponent,
        UtilitiesNewPacketTemplateCardComponent,
    UtilitiesTodoCardComponent
} from "./components";
import
{
    AccountManagerService,
    AddAccountWizardService,
    AlertCodeService,
    ChargeFeeCodeService,
    CostCategoryCodeService,
    EntitySuggestService,
    FormCodeService,
    GroupCodeService,
    IssuedByCodeService,
    JobCodeService,
    MapService,
    MeterSearchService,
    MeterService,
    RateCodeService,
    ReadGroupService,
    ServiceAddressService,
    ServiceCodeService,
    ServiceOrderWizardApiService,
    StaffCodeService,
    TylerSmartMeterService,
    UBAccountLookupService,
    UBAccountService,
    UBDashboardService,
    UtilityBillingDialogService,
} from "./services";

const angularModules = [
    CommonModule,
    FlexModule,
    FlexLayoutModule,
    ReactiveFormsModule,
];

const angularMaterialModules = [
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    MatProgressBarModule,
    MatStepperModule,
    MatTableModule,
];

const lgdModules = [
    CommandsModule,
    CommonComponentsModule,
    CoreModule,
    CommonDialogsModule,
    ChartJsContainerComponent,
    ChartingModule,
    ContactModule,
    LgdCheckboxModule,
    SearchModule,
    IncodeModule,
    CustomViewModule.withComponents(
        /* eslint-disable @typescript-eslint/naming-convention */
        {
            "utilities-packet-progress-chart": PacketsProgressChartsComponent,
            "utilities-bill-cycle-chart": UtilitiesBillCycleChartComponent,
            "utilities-connecting-disconnecting-accounts-chart": UtilitiesConnectingDisconnectingAccountsChartComponent,
            "utilities-outstanding-service-orders-chart": OutstandingServiceOrdersChartComponent,
            "utilities-notification-history-chart": NotificationHistoryChartComponent,
            "utilities-prepaid-billing-chart": PrepaidBillingChartComponent,
            "utilities-prepaid-consumption-chart": PrepaidConsumptionChartComponent,
            "utilities-quick-links-chart": QuickLinksChartComponent,
            "utilities-smart-meter-portal-usage-chart": SmartMeterPortalUsageChartComponent,
            "utilities-total-community-consumption-vs-meter-reporting-chart": TotalCommunityConsumptionVsMeterChartComponent,
            "utilities-total-meter-vs-meter-reporting-chart": TotalMeterReportingVsMeterReportingChartComponent,
            "utilities-new-packet-template-card": UtilitiesNewPacketTemplateCardComponent,
            "utilities-todo-card": UtilitiesTodoCardComponent,
        }
        /* eslint-enable @typescript-eslint/naming-convention */
    ),
    AggregatesModule.withActionHandlers(
        CatchUpBillingActionHandler,
        LeakNotificationNotSentActionHandler,
        PendingPaymentPlanActionHandler,
        TylerSmartMeterActionHandler,
        UtilityServiceRequestActionHandler),
    LgdSelectModule,
    LgdTextModule,
    LgdTypeAheadModule,
    LgdTextAreaModule,
    LgdDatePickerModule,
];

const components = [
    PrepaidBillingChartComponent,
    PrepaidConsumptionChartComponent,
    UtilitiesNewPacketTemplateCardComponent,
    UtilitiesTodoCardComponent,
    QuickLinksChartComponent,
    PacketProgressChartComponent,
    PacketsProgressChartsComponent,
    UtilitiesBillCycleChartComponent,
    OutstandingServiceOrdersChartComponent,
    NotificationHistoryChartComponent,
    SmartMeterPortalUsageChartComponent,
    TotalMeterReportingVsMeterReportingChartComponent,
    UtilitiesConnectingDisconnectingAccountsChartComponent,
    TotalCommunityConsumptionVsMeterChartComponent,
    ContactMatchDialogComponent,
    ExternalLinkPopUpDialogComponent,
    ServiceRequestRejectDialogComponent,
    ServiceRequestTextEmailDialogComponent,
    TylerSmartMeterTextEmailDialogComponent,
    AddTodoItemDialogComponent,
    NewPacketTemplateDialogComponent,
    ServiceAddressWizardDialogComponent,
];

@NgModule({
    imports: [
        ...angularModules,
        ...angularMaterialModules,
        ...lgdModules
    ],
    declarations: components,
    exports: components,
    providers: [
        AccountManagerService,
        AddAccountWizardService,
        AlertCodeService,
        ChargeFeeCodeService,
        CostCategoryCodeService,
        EntitySuggestService,
        FormCodeService,
        GroupCodeService,
        IssuedByCodeService,
        JobCodeService,
        MapService,
        MeterSearchService,
        MeterService,
        RateCodeService,
        ReadGroupService,
        ServiceAddressService,
        ServiceCodeService,
        ServiceOrderWizardApiService,
        StaffCodeService,
        TylerSmartMeterService,
        UBAccountLookupService,
        UBAccountService,
        UBDashboardService,
        UtilityBillingDialogService,
        CatchUpBillingActionHandler,
        LeakNotificationNotSentActionHandler,
        PendingPaymentPlanActionHandler,
        TylerSmartMeterActionHandler,
        UtilityServiceRequestActionHandler
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UtilityBillingModule { }
